<script lang="ts">
    import {
        VpCheckIcon,
        VpChevronDownIcon,
        VpChevronForwardIcon,
        VpCloseIcon,
        VpReturnIcon,
        VpSearchIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createAccordion } from '@melt-ui/svelte';
    import { slide } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import Button from '$saga/components/Button.svelte';
    import type { Option, OptionList } from '$saga/types/search.types';
    import { filterList, flattenOptionTree } from '$saga/utils/searchFilters';
    import CheckboxElement from '$saga/views/Search/components/CheckboxElement.svelte';
    import FilterBadges from '$saga/views/Search/Filters/FilterBadges.svelte';
    import NoResultsFilter from '$saga/views/Search/MainFilters/drawer/NoResultsFilter.svelte';
    import {
        areMainFiltersActive,
        areSecondaryFiltersActive,
        filtersContext,
    } from '$saga/views/Search/store';
    import {
        debouncedHandleFilterChange,
        redirectToGenericSearch,
        shouldRedirectOnRemoval,
    } from '$saga/views/Search/utils';

    // Destination hierarchy: each root (continent) is an accordion, its
    // descendants are shown flat underneath, indented by depth.
    //
    // The backend only sends a flat list (each option carries its own
    // `parentId`/`depth`); the tree is rebuilt here by linking children to
    // their parent id, roots being whatever has no matching parent.
    export let tree: OptionList;
    export let handleValidateSelection: () => void;
    export let values: string[];
    export let labels: string[];
    export let placeholder: string;
    export let allLabel: string | null = null;

    const filterType = 'destination' as const;

    const buildDestinationTree = (options: OptionList): OptionList => {
        const nodesById = new Map<string, Option>(
            options.map((option) => [
                option.value,
                { ...option, children: [] },
            ]),
        );

        const roots: OptionList = [];
        for (const node of nodesById.values()) {
            const parent =
                node.parentId != null
                    ? nodesById.get(String(node.parentId))
                    : undefined;
            (parent ? parent.children! : roots).push(node);
        }

        return roots;
    };

    // Flat list, as received from the backend: used for search and as the
    // source to rebuild the hierarchy below.
    $: flatList = tree;
    $: destinationTree = buildDestinationTree(flatList);

    let searchQuery = '';
    $: filteredOptions = searchQuery
        ? filterList({ query: searchQuery, fullList: flatList })
        : [];
    let isUserSearching = false;
    let inputElement: HTMLInputElement;

    let selectedValue: string[] = values;
    let selectedLabel: string[] = labels;
    $: selectedValue = values;
    $: selectedLabel = labels;

    // Indent scale for nested destinations (t-shirt spacing tokens only).
    const indentClasses = ['', 'pl-l', 'pl-2xl', 'pl-4xl', 'pl-5xl', 'pl-6xl'];
    const indentClass = (depth: number) =>
        indentClasses[Math.min(depth, indentClasses.length - 1)];

    const {
        elements: { content, item, trigger, root },
        helpers: { isSelected },
    } = createAccordion({
        // Open the branch that already contains a selected destination.
        defaultValue: buildDestinationTree(tree)
            .filter((rootOption) =>
                flattenOptionTree([rootOption]).some((option) =>
                    selectedValue.includes(option.value),
                ),
            )
            .map((rootOption) => rootOption.value),
        multiple: true,
    });

    // Ids of every ancestor of `option`, following parentId up to the root.
    $: optionsById = new Map(flatList.map((opt) => [opt.value, opt]));

    const ancestorIds = (option: Option): string[] => {
        const ids: string[] = [];
        let current =
            option.parentId != null ? String(option.parentId) : undefined;
        while (current != null) {
            ids.push(current);
            const parent = optionsById.get(current);
            current =
                parent?.parentId != null ? String(parent.parentId) : undefined;
        }
        return ids;
    };

    const handleSelection = (option: Option) => {
        if (
            selectedValue.find((val) => val === 'all') &&
            option.value !== 'all'
        ) {
            selectedValue = [];
            selectedLabel = [];
        }

        if (selectedValue.includes(option.value)) {
            selectedValue = selectedValue.filter(
                (value) => value !== option.value,
            );
            selectedLabel = selectedLabel.filter(
                (label) => label !== option.label,
            );
        } else {
            // Selecting a deeper destination supersedes any selected ancestor.
            const parentsToDrop = ancestorIds(option);
            // to the same, selecting a parent supersedes any selected descendant.
            const descendantsToDrop = flattenOptionTree([option]).map(
                (opt) => opt.value,
            );

            const keptIndexes = selectedValue.reduce<number[]>(
                (indexes, value, index) =>
                    parentsToDrop.includes(value) ||
                    descendantsToDrop.includes(value)
                        ? indexes
                        : [...indexes, index],
                [],
            );
            selectedValue = [
                ...keptIndexes.map((index) => selectedValue[index]),
                option.value,
            ];
            selectedLabel = [
                ...keptIndexes.map((index) => selectedLabel[index]),
                option.label,
            ];
        }
        values = selectedValue;
        labels = selectedLabel;

        if (
            !$filtersContext.isHomePage &&
            shouldRedirectOnRemoval(filterType, String(option.value))
        ) {
            redirectToGenericSearch($filtersContext.formValues);
            return;
        }
        debouncedHandleFilterChange();
    };

    const onInputChange = (event: Event) => {
        searchQuery = (event.target as HTMLInputElement).value;
        isUserSearching = searchQuery !== '';
    };

    const clearInput = () => {
        if (inputElement) {
            inputElement.value = '';
            searchQuery = '';
            isUserSearching = false;
        }
    };

    const resetSelection = () => {
        selectedValue = [];
        selectedLabel = [];
        values = selectedValue;
        labels = selectedLabel;
        debouncedHandleFilterChange();
    };

    export const validateSelection = () => {
        values = selectedValue;
        labels = selectedLabel;
        handleValidateSelection();
    };
</script>

<div class="flex h-full w-full flex-col">
    <div class="no-scrollbar relative flex flex-1 flex-col overflow-y-scroll">
        {#if $areMainFiltersActive || $areSecondaryFiltersActive}
            <div class="flex flex-col gap-m py-m">
                <FilterBadges />
            </div>
        {/if}

        <!-- Input -->
        <div
            class="relative mb-m flex items-center border-b border-border-neutral"
        >
            <VpSearchIcon
                class="pointer-events-none absolute ml-s size-l text-content-quiet"
            />
            <input
                type="text"
                on:input={onInputChange}
                {placeholder}
                bind:this={inputElement}
            />
            {#if isUserSearching}
                <button
                    type="button"
                    class="absolute right-m mr-s"
                    on:click={clearInput}
                >
                    <VpCloseIcon class="size-l text-content-quiet" />
                </button>
            {/if}
        </div>

        {#if !isUserSearching && allLabel}
            <CheckboxElement
                labelClass="text-content-brand"
                onToggleCheck={() => {
                    if (
                        selectedValue.length === 1 &&
                        selectedValue[0] === 'all'
                    ) {
                        selectedValue = [];
                        selectedLabel = [];
                        return;
                    }
                    selectedValue = ['all'];
                    selectedLabel = [allLabel];
                    values = selectedValue;
                    labels = selectedLabel;

                    debouncedHandleFilterChange();
                }}
                label={allLabel}
                isChecked={selectedValue.length === 1 &&
                    selectedValue[0] === 'all'}
            />
        {/if}

        {#if isUserSearching}
            {#if filteredOptions && filteredOptions.length > 0}
                {#each filteredOptions as filteredOption (filteredOption.value)}
                    <CheckboxElement
                        onToggleCheck={() => handleSelection(filteredOption)}
                        label={`${filteredOption.label} (${filteredOption.count})`}
                        subLabel={ancestorIds(filteredOption)
                            .map(
                                (ancestorId) =>
                                    flatList.find(
                                        (opt) => opt.value === ancestorId,
                                    )?.label ?? null,
                            )
                            .filter((label) => label !== null)
                            .reverse()
                            .join(' > ')}
                        isChecked={selectedValue.includes(filteredOption.value)}
                        isDisabled={filteredOption.count === 0}
                    />
                {/each}
            {:else}
                <NoResultsFilter />
            {/if}
        {:else}
            <div {...$root}>
                {#each destinationTree as rootOption (rootOption.value)}
                    <div {...$item(rootOption.value)} use:item>
                        <button
                            type="button"
                            {...$trigger(rootOption.value)}
                            use:trigger
                            class="flex w-full items-center gap-xs border-t border-solid border-border-quiet py-m"
                        >
                            {#if $isSelected(rootOption.value)}
                                <VpChevronDownIcon class="size-l" />
                            {:else}
                                <VpChevronForwardIcon class="size-l" />
                            {/if}
                            <span>{rootOption.label}</span>
                        </button>
                        {#if $isSelected(rootOption.value)}
                            <div
                                {...$content(rootOption.value)}
                                use:content
                                transition:slide
                            >
                                <CheckboxElement
                                    onToggleCheck={() =>
                                        handleSelection(rootOption)}
                                    label={trans(
                                        'search.filter.sidebar.all_trips_in_destination',
                                        { '%name%': rootOption.label },
                                    )}
                                    isChecked={selectedValue.includes(
                                        rootOption.value,
                                    )}
                                    isDisabled={rootOption.count === 0}
                                />
                                {#each flattenOptionTree(rootOption.children ?? []) as option (option.value)}
                                    <div class={indentClass(option.depth ?? 1)}>
                                        <CheckboxElement
                                            onToggleCheck={() =>
                                                handleSelection(option)}
                                            label={`${option.label} (${option.count})`}
                                            isChecked={selectedValue.includes(
                                                option.value,
                                            )}
                                            isDisabled={option.count === 0}
                                        />
                                    </div>
                                {/each}
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>
        {/if}
    </div>

    <div
        class="sticky bottom-0 left-0 mt-auto flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
    >
        <Button
            size="small"
            intent="secondary"
            state={selectedValue.length === 0 ? 'disabled' : undefined}
            shape="rounded"
            type="button"
            disabled={selectedValue.length === 0}
            on:click={resetSelection}
            startIcon={VpReturnIcon}
        >
            {trans('search.filter.sidebar.clear')}
        </Button>
        <Button
            size="small"
            shape="rounded"
            type="button"
            on:click={() => validateSelection()}
            startIcon={VpCheckIcon}
            state={$filtersContext.isSubmitting ? 'loading' : undefined}
        >
            {trans('search.filter.sidebar.apply')}
        </Button>
    </div>
</div>

<style lang="postcss">
    input {
        appearance: unset;
        display: inline-block !important;
        margin: unset;
        padding: unset;
        max-width: unset;
        min-height: unset;
        outline: unset !important;
        background-color: inherit;
        font-family: inherit;
        font-size: inherit;
        color: inherit !important;
        border: unset;
        border-radius: unset;
        box-shadow: unset !important;
        transition: unset;
        background-position-x: 16px;
        @apply block w-full bg-white px-m py-xs pl-4xl leading-6 placeholder:text-sm placeholder:text-content-quiet;
    }
</style>
